import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag, Loader2, Trash2, ArrowRight, Truck, Shield, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, loading, updateQuantity, removeFromCart, clearCart } = useCart();
  const [updatingItem, setUpdatingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const cartItems = cart.items || [];

  const handleClearCart = async () => {
    setClearConfirm(false);
    await clearCart();
    toast.success('Cart Cleared', {
      description: 'All items have been removed from your cart.'
    });
  };

  const subtotal = cart.subtotal || 0;
  const shipping = subtotal >= 75 ? 0 : (subtotal > 0 ? 8.99 : 0);
  const total = subtotal + shipping;

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdatingItem(productId);
    await updateQuantity(productId, newQuantity);
    setUpdatingItem(null);
  };

  const handleRemove = async (productId) => {
    setDeleteConfirm(null);
    await removeFromCart(productId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#5D6B55]" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-[#F5F1EB] rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-12 w-12 text-[#8B7355]" />
          </div>
          <h2 className="text-3xl font-bold text-[#2C2824] mb-4">Your Cart is Empty</h2>
          <p className="text-[#6B6560] mb-8">Looks like you haven't added any items yet. Discover our collection!</p>
          <Link to="/shop">
            <Button className="bg-[#059669] hover:bg-[#047857] text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5">
              Start Shopping
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-8 md:py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-[#2C2824] mb-8">Shopping Cart</h1>

        {/* Clear Cart Button */}
        {cartItems.length > 1 && (
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              onClick={() => setClearConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Clear All ({cartItems.length} items)
            </Button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => {
              const product = item.product || {};
              const displayPrice = product.sale_price || product.price || 0;
              const imageUrl = product.images?.[0] || 'https://via.placeholder.com/96';
              
              return (
                <div 
                  key={item.product_id} 
                  className="bg-white rounded-xl p-4 md:p-6 border border-[#E8E4DE] shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row gap-4 md:gap-6"
                >
                  {/* Image */}
                  <div className="w-full md:w-28 h-32 md:h-28 bg-[#F5F1EB] rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <Link 
                        to={`/shop/product/${product.slug}`}
                        className="font-semibold text-[#2C2824] hover:text-[#5D6B55] transition-colors line-clamp-2 text-sm md:text-base"
                      >
                        {product.name}
                      </Link>
                      
                      {/* Delete Button - Desktop */}
                      <button
                        onClick={() => setDeleteConfirm(item.product_id)}
                        className="hidden md:flex p-2 hover:bg-red-50 rounded-lg transition-colors group"
                        title="Remove item"
                      >
                        <Trash2 className="h-5 w-5 text-[#9CA3AF] group-hover:text-red-500 transition-colors" />
                      </button>
                    </div>
                    
                    <p className="text-[#5D6B55] font-semibold mt-1">${displayPrice.toFixed(2)}</p>

                    <div className="flex items-center justify-between mt-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-1 bg-[#F5F1EB] rounded-lg p-1">
                        <button
                          onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1 || updatingItem === item.product_id}
                          className="p-2 hover:bg-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Minus className="h-4 w-4 text-[#2C2824]" />
                        </button>
                        <span className="w-10 text-center font-semibold text-[#2C2824]">
                          {updatingItem === item.product_id ? (
                            <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                          ) : (
                            item.quantity
                          )}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                          disabled={updatingItem === item.product_id}
                          className="p-2 hover:bg-white rounded-md transition-colors disabled:opacity-50"
                        >
                          <Plus className="h-4 w-4 text-[#2C2824]" />
                        </button>
                      </div>

                      {/* Item Total & Mobile Delete */}
                      <div className="flex items-center gap-3">
                        <p className="font-bold text-[#2C2824] text-lg">
                          ${item.item_total?.toFixed(2)}
                        </p>
                        
                        {/* Delete Button - Mobile */}
                        <button
                          onClick={() => setDeleteConfirm(item.product_id)}
                          className="md:hidden p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-5 w-5 text-[#9CA3AF]" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 border border-[#E8E4DE] shadow-sm sticky top-24">
              <h2 className="text-xl font-bold text-[#2C2824] mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#6B6560]">
                  <span>Subtotal ({cart.item_count} items)</span>
                  <span className="font-medium text-[#2C2824]">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#6B6560]">
                  <span>Shipping</span>
                  <span className={`font-medium ${shipping === 0 ? 'text-[#059669]' : 'text-[#2C2824]'}`}>
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                
                {/* Free shipping progress */}
                {subtotal > 0 && subtotal < 75 && (
                  <div className="bg-[#FFF8E7] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="h-4 w-4 text-[#D4A574]" />
                      <span className="text-sm font-medium text-[#8B7355]">
                        ${(75 - subtotal).toFixed(2)} away from FREE shipping!
                      </span>
                    </div>
                    <div className="w-full bg-[#E8E4DE] rounded-full h-2">
                      <div 
                        className="bg-[#D4A574] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((subtotal / 75) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {subtotal >= 75 && (
                  <div className="flex items-center gap-2 text-[#059669] bg-green-50 rounded-lg p-3">
                    <Truck className="h-4 w-4" />
                    <span className="text-sm font-medium">You qualify for FREE shipping!</span>
                  </div>
                )}
                
                <div className="border-t border-[#E8E4DE] pt-4 flex justify-between text-lg font-bold text-[#2C2824]">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {/* CHECKOUT BUTTON - PROMINENT */}
              <Button 
                onClick={() => navigate('/checkout')}
                className="w-full bg-[#059669] hover:bg-[#047857] text-white py-7 text-lg font-bold rounded-xl shadow-[0px_4px_12px_rgba(5,150,105,0.3)] hover:shadow-[0px_6px_16px_rgba(5,150,105,0.4)] transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Link 
                to="/shop" 
                className="block text-center mt-4 text-[#5D6B55] hover:text-[#4A5745] font-medium transition-colors"
              >
                Continue Shopping
              </Link>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-[#E8E4DE] flex items-center justify-center gap-4 text-[#6B6560]">
                <div className="flex items-center gap-1 text-xs">
                  <Shield className="h-4 w-4 text-[#5D6B55]" />
                  <span>Secure Checkout</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <Truck className="h-4 w-4 text-[#5D6B55]" />
                  <span>30-Day Returns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-[#2C2824] mb-2">Remove from Cart?</h3>
              <p className="text-[#6B6560] mb-6">
                {cartItems.find(i => i.product_id === deleteConfirm)?.product?.name?.substring(0, 50)}...
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 py-3 border-[#E8E4DE] text-[#2C2824] hover:bg-[#F5F1EB]"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleRemove(deleteConfirm)}
                  className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white"
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Cart Confirmation Modal */}
      {clearConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-[#2C2824] mb-2">Clear Entire Cart?</h3>
              <p className="text-[#6B6560] mb-6">
                This will remove all {cartItems.length} items from your cart. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setClearConfirm(false)}
                  className="flex-1 py-3 border-[#E8E4DE] text-[#2C2824] hover:bg-[#F5F1EB]"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleClearCart}
                  className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white"
                >
                  Clear All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
